import { ColorV2, Text, TagSelector } from "@adoptaunabuelo/react-components";
import { Cake, MapPin } from "lucide-react";
import { useState } from "react";
import styled from "styled-components";
import type { FelicitacionStepProps } from "./config";
import { Constant } from "client-v2";
import grandpaDefaultImage from "../../../assets/images/icon/grandpa_icon.png";
import grandmaDefaultImage from "../../../assets/images/icon/grandma_icon.png";

const StepContent = styled.div`
	text-align: left;
	display: flex;
	height: 100%;
	min-height: 0;
	flex-direction: column;
	background: ${ColorV2.surface.invert};
	overflow-y: auto;
	overflow-x: hidden;
	-webkit-overflow-scrolling: touch;
	box-sizing: border-box;
	padding: 24px;
`;

const Divider = styled.div`
	width: 100%;
	height: 1px;
	background: var(--border-clear-neutral-soft, rgba(0, 29, 61, 0.10));
	margin: 32px 0 36px;
`;

const GrandpaSummary = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) 120px;
	gap: 24px;
	align-items: start;
`;

const GrandpaImage = styled.img`
	width: 120px;
	height: 120px;
	object-fit: cover;
	border-radius: 24px;
`;

const GrandpaMeta = styled.div`
	display: flex;
	min-width: 0;
	flex-direction: column;
	gap: 8px;
`;

const MetaItem = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 8px;
	min-width: 0;

	svg {
		flex-shrink: 0;
		margin-top: 4px;
	}
`;

const MetaText = styled.div`
	display: flex;
	min-width: 0;
	flex-direction: column;
	gap: 4px;
`;

const Section = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 16px;
	max-width: 640px;
	margin-top: 24px;
`;

const HobbiesContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;

	& > * {
		margin: 0 !important;
	}
`;

const LinkButton = styled.button`
	border: 0;
	padding: 0;
	width: fit-content;
	background: transparent;
	color: ${ColorV2.text.primary};
	font: inherit;
	font-weight: 600;
	text-decoration: underline;
	cursor: pointer;
`;

const IconColor = "#0A84E3";
const STORY_PREVIEW_LENGTH = 180;

const getFormattedBirthday = (iso?: string) => {
	if (!iso) return "Fecha pendiente";

	return new Intl.DateTimeFormat("es-ES", {
		day: "numeric",
		month: "long",
		year: "numeric",
	}).format(new Date(iso));
};

const getAgeLabel = (iso?: string) => {
	if (!iso) return "Edad pendiente";

	const birthDate = new Date(iso);
	const today = new Date();
	let age = today.getFullYear() - birthDate.getFullYear();
	const monthDiff = today.getMonth() - birthDate.getMonth();

	if (
		monthDiff < 0 ||
		(monthDiff === 0 && today.getDate() < birthDate.getDate())
	) {
		age -= 1;
	}

	return `${age} años`;
};

const getGrandpaStory = (
	selectedGrandpa: NonNullable<FelicitacionStepProps["selectedGrandpa"]>,
) => {
	if (typeof selectedGrandpa.live?.history === "string") {
		return selectedGrandpa.live.history;
	}

	const hobbies = Array.isArray(selectedGrandpa.data?.hobbies)
		? selectedGrandpa.data?.hobbies ?? []
		: [];
	const hasLetter = selectedGrandpa.data?.letter;
	const shortAddress =
		selectedGrandpa.shortAddress ?? selectedGrandpa.address ?? "su localidad";
	const hobbyText = hobbies.length
		? `Le gusta especialmente ${hobbies.slice(0, 3).join(", ")}.`
		: "Estamos preparando más detalles sobre sus aficiones.";

	return `${selectedGrandpa.name ?? "Esta persona mayor"} vive en ${shortAddress}. ${
		hasLetter
			? "Le hace mucha ilusión recibir cartas y detalles especiales."
			: "Muy pronto compartiremos más información sobre su historia."
	} ${hobbyText}`;
};

const Step1 = ({ selectedGrandpa }: FelicitacionStepProps) => {
	const [showFullStory, setShowFullStory] = useState(false);

	if (!selectedGrandpa) {
		return (
			<StepContent>
				<Text type="h3" weight="semibold">
					¡Te presentamos al cumpleañero!
				</Text>
				<Text type="p">
					No hemos podido cargar la información del abuelo todavía.
				</Text>
			</StepContent>
		);
	}

	const hobbies = Array.isArray(selectedGrandpa.data?.hobbies)
		? selectedGrandpa.data?.hobbies ?? []
		: [];
	const foundHobbies = hobbies.length
		? Constant.hobbies().filter((hobby) =>
				hobbies.includes(hobby.id)
		  )
		: [];
	const imageSrc =
		typeof selectedGrandpa.image === "string"
			? selectedGrandpa.image
			: selectedGrandpa.image?.url ??
			  (selectedGrandpa.gender === "female"
					? grandmaDefaultImage
					: grandpaDefaultImage);
	const birthdayIso = selectedGrandpa.birthday?.iso;
	const location =
		selectedGrandpa.shortAddress ??
		selectedGrandpa.address ??
		"Ubicación pendiente";
	const story = getGrandpaStory(selectedGrandpa);
	const hasLongStory = story.length > STORY_PREVIEW_LENGTH;
	const visibleStory =
		hasLongStory && !showFullStory
			? `${story.slice(0, STORY_PREVIEW_LENGTH).trimEnd()}...`
			: story;

	return (
		<StepContent>
			<Text type="h3" weight="semibold">
				¡Te presentamos al cumpleañero!
			</Text>

			<Text
				type="c1"
				weight="regular"
				style={{
					color: "var(--text-clear-neutral-medium, rgba(0, 29, 61, 0.56))",
					marginTop: 8,
				}}
			>
				Por favor, por su privacidad no compartas su información ni su foto en
				redes sociales. ¡Gracias!
			</Text>

			<Divider />

			<GrandpaSummary>
				<GrandpaMeta>
					<Text
						type="h3"
						weight="semibold"
						style={{ wordBreak: "break-word" }}
					>
						{selectedGrandpa.name}
					</Text>

					<MetaItem>
						<Cake color={IconColor} size={20} strokeWidth={2.2} />
						<MetaText>
							<Text type="p" weight="regular">
								{getFormattedBirthday(birthdayIso)}
							</Text>
							<Text type="p2" weight="semibold">
								{getAgeLabel(birthdayIso)}
							</Text>
						</MetaText>
					</MetaItem>

					<MetaItem>
						<MapPin color={IconColor} size={20} strokeWidth={2.2} />
						<MetaText>
							<Text type="p" weight="regular">
								{location}
							</Text>
						</MetaText>
					</MetaItem>
				</GrandpaMeta>

				<GrandpaImage alt={selectedGrandpa.name} src={imageSrc} />
			</GrandpaSummary>

			<Section>
				<Text type="h4" weight="semibold">
					Su historia
				</Text>

				<Text type="p">{visibleStory}</Text>

				{hasLongStory && !showFullStory && (
					<LinkButton onClick={() => setShowFullStory(true)} type="button">
						Leer más
					</LinkButton>
				)}
			</Section>

			<Section>
				<Text type="h4" weight="semibold">
					Aficiones
				</Text>
				<HobbiesContainer>
					{foundHobbies.map((hobby) => (
						<TagSelector
							design="design2"
							key={hobby.id}
							options={[foundHobbies.find((h) => h.id === hobby.id)!]}
							onlyVisual={true}
						/>
					))}
				</HobbiesContainer>
			</Section>
		</StepContent>
	);
};

export default Step1;
